/**
 * The bulk-upload scheduler and state machine. Every piece of I/O is injected,
 * so this file has no React, no supabase-js and no DOM - and therefore no
 * excuse for an untested branch.
 *
 * Two invariants are worth restating here because they are easy to "simplify"
 * away:
 *
 *  - THREE slots own decode + PUT; the INSERT leg is serialized behind a
 *    single-permit lock. public.photos carries the admin audit trigger, which
 *    takes FOR UPDATE on one chain row; parallel inserts make a CONCURRENT
 *    admin's save fail on lock_timeout, not merely wait.
 *  - EVERY non-success INSERT outcome - error, timeout, or a 2xx with no id -
 *    removes the object that was just written. When that removal also fails the
 *    path is unrecoverable (storage.objects has no SELECT policy, so nothing can
 *    list it), so it is collected in orphanPaths and console.warn'd.
 *
 * ABORT AND CANCEL MEAN THE SAME THING FOR WORK ALREADY IN FLIGHT (spec 4.3):
 * both stop STARTING new files - no row leaves `queued` - and neither is a
 * rollback. A row already in processing/uploading/saving runs to a TERMINAL
 * state with its result accounted: a committed INSERT is `done` and its id is
 * reported, a landed PUT whose INSERT then failed still runs the compensating
 * remove. So `aborted` is checked in worker() - where new work is picked up -
 * and NOWHERE in the late-result guards, which only ever discard a SUPERSEDED
 * attempt. Folding `aborted` into those guards silently loses committed photos
 * and leaks un-listable objects.
 */
import {
  CACHE_CONTROL,
  CONSECUTIVE_FAILURE_ABORT,
  INSERT_TIMEOUT_MS,
  UPLOAD_TIMEOUT_MS,
  buildStoragePath,
  classifyFailure,
  createdAtForIndex,
  describeError,
  extForMime,
  processingFailureReason,
  validatePublicUrl,
} from './helpers'
import type { ProcessedFile, ProcessFile } from './pipeline'

const DASH = '—'

export const PAUSE_AUTH_COPY =
  `Your session expired. Sign in again in another tab, then press Resume ${DASH} your file list is kept.`
export const PAUSE_NETWORK_COPY = `Upload paused ${DASH} the connection dropped. Resume when you are back online.`
export const PAUSE_ALBUM_GONE_COPY = `That album no longer exists ${DASH} pick another album and resume.`
export const ABORT_PERMISSION_COPY = 'Your admin permissions changed — remaining files were not uploaded.'

export type RowState =
  | 'queued'
  | 'processing'
  | 'uploading'
  | 'saving'
  | 'done'
  | 'failed'
  | 'skipped'
  | 'cancelled'

/**
 * Once a row is here it is ACCOUNTED FOR and nothing may move it: an outcome
 * already counts it, and for `done` the photo is live in the album.
 */
const TERMINAL: readonly RowState[] = ['done', 'failed', 'skipped', 'cancelled']

export interface BulkRow {
  /** Stable React key. */
  id: string
  file: File
  name: string
  size: number
  label: string
  state: RowState
  reason?: string
  hash?: string | null
  asIs?: boolean
  metadataRetained?: boolean
  /** Narrower than asIs: the encode ran and the original still won. See ProcessedFile. */
  keptOriginal?: boolean
  previewUrl?: string | null
  /** Last storage path attempted. */
  path?: string
  /** Set on done. */
  photoId?: string
  attempt: number
  /** For the ">10s, still uploading" elapsed counter. */
  startedAtMs?: number
  /** Set by keepAnyway(): exempt this row from the in-batch hash check. */
  keepAnyway?: boolean
}

export interface UploadOptions {
  upsert: false
  cacheControl: string
  contentType: string
}

export interface PhotoInsert {
  album_id: string
  label: string
  url: string
  uploaded_by: string
  created_at: string
  moderation_status: 'approved'
}

export interface QueueDeps {
  uploadObject(path: string, body: Blob, options: UploadOptions): Promise<{ error: unknown | null }>
  getPublicUrl(path: string): string
  insertPhoto(row: PhotoInsert): Promise<{ id: string | null; error: unknown | null }>
  removeObject(path: string): Promise<{ error: unknown | null }>
  process: ProcessFile
  now(): number
  storageOrigin: string
}

export type PauseReason =
  | { kind: 'auth'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'album-gone'; message: string }

export interface QueueConfig {
  /** The batch's INITIAL destination. setAlbumId() re-points every row not yet uploaded. */
  albumId: string
  uploadedBy: string
  batchStartMs: number
  /** Default 3. */
  slots?: number
  onRow(id: string, patch: Partial<BulkRow>): void
  onPause(reason: PauseReason): void
  onAbort(reason: string): void
}

export interface BatchOutcome {
  done: number
  failed: number
  skipped: number
  cancelled: number
  asIsCount: number
  photoIds: string[]
  /** PUT succeeded, row absent, remove() also failed. Manual cleanup only. */
  orphanPaths: string[]
  errorCodes: string[]
  uploadedBytes: number
}

export interface QueueHandle {
  start(): Promise<BatchOutcome>
  cancel(): void
  /** Clears the pause flag only; the caller then calls start() again. */
  resume(): void
  /**
   * Re-point the batch at another album. This is what makes an `album-gone`
   * pause RECOVERABLE: the config's albumId is baked in at construction and
   * feeds both buildStoragePath and the INSERT payload, so without this a
   * Resume re-targets the deleted album, re-fails 23503 and re-pauses forever -
   * and the only escape was a reload, which discards the queue the pause copy
   * promises to keep. Applies to every row that has not been uploaded yet;
   * rows already `done` keep the album they committed to.
   */
  setAlbumId(albumId: string): void
  /** Un-skip a duplicate and exempt it from the hash check. */
  keepAnyway(id: string): void
  isRunning(): boolean
}

const TIMED_OUT = Symbol('timed-out')

/**
 * The hard ceiling on how long ONE row may hold the single INSERT permit: the
 * row's own timeout plus one more of the same length as grace. See
 * serializedInsert for why the grace exists and why it is safe to hand the
 * permit on once it expires.
 */
const PERMIT_HOLD_MS = INSERT_TIMEOUT_MS + INSERT_TIMEOUT_MS

function withTimeout<T>(work: Promise<T>, ms: number): Promise<T | typeof TIMED_OUT> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve(TIMED_OUT), ms)
    work.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      },
    )
  })
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

const isDuplicate = (e: unknown): boolean => {
  const text = describeError(e).toLowerCase()
  return text.includes('duplicate') || text.includes('already exists')
}

/**
 * Release a preview object URL without depending on a DOM. The queue is pure and
 * runs in jsdom and in Node, so the call is optional-chained and guarded: a host
 * without URL.revokeObjectURL simply keeps its (non-existent) blob.
 */
const revokePreview = (url: string | null | undefined): void => {
  if (!url) return
  try {
    globalThis.URL?.revokeObjectURL?.(url)
  } catch {
    // A host with no object-URL registry. Nothing to release.
  }
}

export function createUploadQueue(rows: BulkRow[], deps: QueueDeps, config: QueueConfig): QueueHandle {
  const slots = config.slots ?? 3
  // seenHashes and orphanPaths are CUMULATIVE across start() calls on purpose:
  // a file already deduped stays deduped, and an orphan recorded on the first
  // pass is still un-listable after a clean second pass (EC-20 forbids ever
  // reporting a clean run while one exists). errorCodes and uploadedBytes are
  // per-pass and are reset by start(); photoIds is derived from the rows.
  const seenHashes = new Map<string, { rowId: string; name: string }>()
  const orphanPaths: string[] = []
  const errorCodes: string[] = []
  // Paths whose landed object has already been compensated for - deleted, or
  // recorded in orphanPaths because the delete itself failed. The post-PUT
  // catch below consults this so a throw arriving AFTER saveRow already cleaned
  // up cannot delete the same object twice or report it twice. Paths are unique
  // per attempt, so this never collides across passes.
  const compensated = new Set<string>()

  // MUTABLE, and read LAZILY at both use sites (buildStoragePath and the INSERT
  // payload) rather than captured per pass: setAlbumId() has to reach every row
  // still to be uploaded, including ones already queued behind a pause.
  let albumId = config.albumId

  let uploadedBytes = 0
  let running = false
  let cancelled = false
  let paused = false
  let aborted = false
  let consecutiveNetwork = 0
  let lastCode = ''
  let consecutiveSameCode = 0
  let inFlight: Promise<BatchOutcome> | null = null
  let insertChain: Promise<unknown> = Promise.resolve()

  /** A caller callback that throws must never brick the queue. */
  const emit = (label: string, call: () => void): void => {
    try {
      call()
    } catch (e) {
      // The REPORTING can throw too - describeError walks an arbitrary object,
      // and a host may stub console. Containing everything is this function's
      // entire job, so the report gets its own guard rather than becoming the
      // one throw that escapes.
      try {
        console.warn(`[bulk-upload] ${label} callback failed`, describeError(e))
      } catch {
        // Nothing left to report with. Swallow it.
      }
    }
  }

  /**
   * Inside a worker `patch` deliberately LETS an onRow throw escape: runRow's
   * catch-all turns it into a failed row with a reason the operator can read,
   * which beats a row that silently claims success while the page never
   * rendered it. Outside a worker use patchQuietly - there is no row-level
   * failure to attribute a throw to there.
   */
  const patch = (row: BulkRow, next: Partial<BulkRow>): void => {
    Object.assign(row, next)
    config.onRow(row.id, next)
  }

  const patchQuietly = (row: BulkRow, next: Partial<BulkRow>): void => {
    Object.assign(row, next)
    emit('onRow', () => config.onRow(row.id, next))
  }

  const fail = (row: BulkRow, reason: string): void => {
    patch(row, { state: 'failed', reason })
  }

  const pause = (reason: PauseReason): void => {
    if (paused || aborted) return
    paused = true
    emit('onPause', () => config.onPause(reason))
  }

  const abort = (message: string): void => {
    if (aborted) return
    aborted = true
    emit('onAbort', () => config.onAbort(message))
  }

  const codeOf = (e: unknown): string => {
    const o = e && typeof e === 'object' ? (e as Record<string, unknown>) : {}
    return String(o.code ?? o.statusCode ?? o.status ?? describeError(e))
  }

  const recordFailure = (e: unknown): void => {
    const code = codeOf(e)
    errorCodes.push(code)
    if (code === lastCode) consecutiveSameCode += 1
    else {
      lastCode = code
      consecutiveSameCode = 1
    }
  }

  /**
   * A row that reached `done` breaks BOTH failure streaks. Without this,
   * CONSECUTIVE_FAILURE_ABORT fires on five same-code failures SPREAD ACROSS a
   * batch that is otherwise succeeding - one odd file class failing now and
   * then burns every remaining row. "Consecutive" has to mean consecutive.
   */
  const recordSuccess = (): void => {
    consecutiveNetwork = 0
    lastCode = ''
    consecutiveSameCode = 0
  }

  const maybeAbortOnRepeats = (reason: string): void => {
    if (consecutiveSameCode >= CONSECUTIVE_FAILURE_ABORT) {
      abort(`Stopping after ${CONSECUTIVE_FAILURE_ABORT} consecutive failures ${DASH} ${reason}`)
    }
  }

  /**
   * The single permit for the INSERT leg. Three rules, all load-bearing:
   *
   *  - The permit is held against the RAW call and released when THAT settles,
   *    never when a timeout race settles. A timed-out insert is still executing
   *    server-side, and handing the permit on would put two or three writers on
   *    the audit chain's FOR UPDATE row: the exact contention this lock exists
   *    to prevent.
   *  - INSERT_TIMEOUT_MS is counted from the moment the permit is GRANTED, not
   *    from the moment the row asked for it. Otherwise one stalled head insert
   *    burns the clock of every row queued behind it: each is failed as "timed
   *    out" and has its object removed, and its INSERT is then dispatched all
   *    the same - committing a photos row whose url points at a path we have
   *    already deleted.
   *  - The hold is BOUNDED. The permit is released when the real call settles
   *    OR PERMIT_HOLD_MS after it was granted - one INSERT_TIMEOUT_MS grace on
   *    top of the row's own timeout - whichever comes first. Without the
   *    ceiling, a fetch that neither answers nor rejects wedges every row behind
   *    it, start() never resolves and the page's Start button never re-enables.
   *    Handing the permit on after the grace is safe because the thing being
   *    serialized against is already dead: `authenticated` carries
   *    statement_timeout=8s and `authenticator` lock_timeout=8s, so the server
   *    kills any statement or lock wait 8s in - long before the 30s client
   *    timeout, let alone the 60s release. Past the grace there is nothing left
   *    on the audit chain's FOR UPDATE row to collide with, and the batch's
   *    liveness wins. Nothing else moves: the stalled row still fails at
   *    INSERT_TIMEOUT_MS with its own reason and its own compensating remove,
   *    and a late answer is still discarded by saveRow's superseded-attempt
   *    guard, which never demotes a terminal row.
   */
  const serializedInsert = (
    payload: PhotoInsert,
  ): Promise<{ id: string | null; error: unknown | null } | typeof TIMED_OUT> => {
    let granted!: () => void
    const permit = new Promise<void>((resolve) => {
      granted = resolve
    })
    const run = () => {
      granted()
      return deps.insertPhoto(payload)
    }
    const raw = insertChain.then(run, run)
    // Both clocks start at the GRANT, not at the request: `permit` gates each
    // race, so a row waiting its turn burns neither its own timeout nor the
    // chain's grace. withTimeout clears its timer when `raw` settles, so the
    // normal path releases the permit at settlement and the grace never fires.
    insertChain = permit.then(() =>
      withTimeout(raw, PERMIT_HOLD_MS).then(
        () => undefined,
        () => undefined,
      ),
    )
    return permit.then(() => withTimeout(raw, INSERT_TIMEOUT_MS))
  }

  /**
   * EC-39. A PUT that timed out or died mid-flight may well have landed, and
   * nothing can ask: storage.objects has no SELECT policy, so the bucket cannot
   * be listed. The retry uses a fresh path, so the abandoned one is recorded
   * CONSERVATIVELY - a false entry costs the operator one look, a missing one is
   * a file nobody can ever find. Deleting it is not an option either: the object
   * may not exist, and a delete of a path we never confirmed teaches nothing.
   */
  const recordPossibleOrphan = (path: string): void => {
    orphanPaths.push(path)
    console.warn(`[bulk-upload] orphan ${path}`, 'upload never confirmed; the object may exist')
  }

  /** Compensating delete. Never throws; an un-deletable object becomes a reported orphan. */
  const cleanup = async (row: BulkRow, path: string): Promise<void> => {
    compensated.add(path)
    try {
      const { error } = await deps.removeObject(path)
      if (error) throw error
    } catch (e) {
      orphanPaths.push(path)
      console.warn(`[bulk-upload] orphan ${path}`, describeError(e))
      return
    }
    // AFTER the try, and quietly: patch() deliberately lets an onRow throw
    // escape, so clearing the path inside the try turned a page-callback failure
    // into a reported orphan for an object that was genuinely deleted.
    patchQuietly(row, { path: undefined })
  }

  const handleUploadError = (row: BulkRow, e: unknown, path: string): void => {
    const cls = classifyFailure(e)
    const reason = describeError(e)
    recordFailure(e)

    if (cls === 'auth') {
      patch(row, { state: 'queued', reason: undefined })
      pause({ kind: 'auth', message: PAUSE_AUTH_COPY })
      return
    }
    if (cls === 'network') {
      // No HTTP status came back, so the PUT may have completed anyway.
      recordPossibleOrphan(path)
      consecutiveNetwork += 1
      fail(row, reason)
      if (consecutiveNetwork >= 3) pause({ kind: 'network', message: PAUSE_NETWORK_COPY })
      return
    }
    // Anything that came back with a status proves the connection is up.
    consecutiveNetwork = 0
    if (cls === 'permission') {
      fail(row, reason)
      abort(ABORT_PERMISSION_COPY)
      return
    }
    fail(row, reason)
    maybeAbortOnRepeats(reason)
  }

  const saveRow = async (
    row: BulkRow,
    attempt: number,
    path: string,
    publicUrl: string,
    index: number,
    retried = false,
  ): Promise<void> => {
    const payload: PhotoInsert = {
      album_id: albumId,
      label: row.label,
      url: publicUrl,
      uploaded_by: config.uploadedBy,
      created_at: createdAtForIndex(config.batchStartMs, index),
      // Explicit, so the intent is legible in the row AND in the audit snapshot.
      moderation_status: 'approved',
    }

    let result: { id: string | null; error: unknown | null } | typeof TIMED_OUT
    try {
      result = await serializedInsert(payload)
    } catch (e) {
      result = { id: null, error: e }
    }
    // A SUPERSEDED attempt is the only thing worth discarding here. An abort must
    // not skip the bookkeeping of an INSERT that PostgREST has already run: the
    // photo is live in the album, and a row left in `saving` is unrecoverable
    // because start()'s pending filter deliberately excludes that state.
    if (row.attempt !== attempt) return

    if (result !== TIMED_OUT && !result.error && result.id) {
      console.info(`[bulk-upload] row ${result.id} ${path}`)
      // Streaks are reset BEFORE the patch: patch() calls the page's onRow, and
      // a throw from there must not leave the batch one failure closer to a
      // spurious CONSECUTIVE_FAILURE_ABORT over a row that actually succeeded.
      recordSuccess()
      patch(row, { state: 'done', photoId: result.id, reason: undefined })
      return
    }

    const error =
      result === TIMED_OUT
        ? { message: `insert timed out after ${INSERT_TIMEOUT_MS}ms` }
        : (result.error ?? { message: 'insert did not return a row' })

    // One short backoff for the transient classes (audit-chain lock, statement timeout)
    // BEFORE any cleanup - the object is still needed if the retry succeeds.
    if (result !== TIMED_OUT && !retried && classifyFailure(error) === 'retryable') {
      await sleep(500)
      if (row.attempt !== attempt) return
      return saveRow(row, attempt, path, publicUrl, index, true)
    }

    await cleanup(row, path)

    const cls = classifyFailure(error)
    const reason = describeError(error)
    recordFailure(error)

    if (cls === 'auth') {
      patch(row, { state: 'queued', reason: undefined })
      pause({ kind: 'auth', message: PAUSE_AUTH_COPY })
      return
    }
    if (cls === 'album-gone') {
      patch(row, { state: 'queued', reason: undefined })
      pause({ kind: 'album-gone', message: PAUSE_ALBUM_GONE_COPY })
      return
    }
    if (cls === 'permission') {
      fail(row, reason)
      abort(ABORT_PERMISSION_COPY)
      return
    }
    fail(row, result === TIMED_OUT ? `timed out ${DASH} retry` : reason)
    maybeAbortOnRepeats(reason)
  }

  const runOneRow = async (row: BulkRow): Promise<void> => {
    const index = rows.indexOf(row)
    // Through patch(), not a bare mutation: onRow is the page's ONLY channel, and
    // a UI that never sees the increment renders "attempt 1" forever.
    patch(row, { attempt: row.attempt + 1 })
    const attempt = row.attempt
    // Attempt only. An aborted row still runs to a terminal state - see the
    // module header. worker() is where `aborted` stops NEW work.
    const stale = () => row.attempt !== attempt

    patch(row, { state: 'processing', reason: undefined, startedAtMs: undefined })

    let processed: ProcessedFile
    try {
      processed = await deps.process(row.file)
    } catch (e) {
      fail(row, processingFailureReason(e))
      return
    }
    if (stale()) return

    if (processed.hash && !row.keepAnyway) {
      const owner = seenHashes.get(processed.hash)
      // A row is NEVER a duplicate of itself. seenHashes is cumulative across
      // start() calls, so on a reused queue (Retry, Resume) a row that already
      // decoded once re-hashes straight onto the entry IT registered.
      if (owner && owner.rowId !== row.id) {
        patch(row, { state: 'skipped', reason: `duplicate of ${owner.name}`, hash: processed.hash })
        return
      }
      if (!owner) seenHashes.set(processed.hash, { rowId: row.id, name: row.name })
    }
    // Each run mints a fresh preview blob; over a Retry of a large batch the
    // outgoing ones are a live megabyte per row (pipeline.ts: "caller must revoke").
    if (row.previewUrl && row.previewUrl !== processed.previewUrl) revokePreview(row.previewUrl)
    patch(row, {
      hash: processed.hash,
      asIs: processed.asIs,
      metadataRetained: processed.metadataRetained,
      keptOriginal: processed.keptOriginal,
      previewUrl: processed.previewUrl,
    })

    // Prod's bucket carries allowed_mime_types; a typeless body is a 400 AFTER RLS
    // would have allowed it. Refuse it here, with a reason an operator can act on.
    const contentType = processed.body.type || ''
    if (!contentType.startsWith('image/')) {
      fail(row, `unsupported content type: ${contentType || 'unknown'}`)
      return
    }

    let path: string
    try {
      path = buildStoragePath(albumId, new Date(deps.now()), processed.mime)
    } catch (e) {
      fail(row, describeError(e))
      return
    }
    patch(row, { state: 'uploading', path, startedAtMs: deps.now() })

    const options: UploadOptions = { upsert: false, cacheControl: CACHE_CONTROL, contentType }
    const put = (target: string) => withTimeout(deps.uploadObject(target, processed.body, options), UPLOAD_TIMEOUT_MS)

    let result: { error: unknown | null } | typeof TIMED_OUT
    try {
      result = await put(path)
    } catch (e) {
      result = { error: e }
    }
    if (stale()) return

    // A Duplicate on a first attempt is a path collision, not an operator problem.
    if (result !== TIMED_OUT && result.error && isDuplicate(result.error)) {
      try {
        path = buildStoragePath(albumId, new Date(deps.now()), processed.mime)
      } catch (e) {
        fail(row, describeError(e))
        return
      }
      patch(row, { path })
      try {
        result = await put(path)
      } catch (e) {
        result = { error: e }
      }
      if (stale()) return
    }

    if (result === TIMED_OUT) {
      // The PUT may have completed server-side after we stopped waiting - EC-39.
      recordPossibleOrphan(path)
      fail(row, `timed out ${DASH} retry`)
      return
    }
    if (result.error) {
      handleUploadError(row, result.error, path)
      return
    }

    consecutiveNetwork = 0
    uploadedBytes += processed.body.size
    console.info(`[bulk-upload] put ${path}`)

    // THE OBJECT EXISTS FROM HERE ON, so this whole region is finally-shaped
    // (EC-19: "in a finally-shaped path so an exception cannot skip it"). Every
    // handled failure below cleans up on its own; the catch is for the ones
    // nobody handles - a dep that throws, or the page's onRow throwing from
    // inside patch(). Without it a landed object walks out with no delete and
    // no orphan record, in a bucket that has no SELECT policy and so cannot be
    // listed. cleanup() never throws, and `compensated` keeps it from acting
    // twice on a path saveRow already dealt with.
    try {
      let publicUrl: string
      try {
        publicUrl = deps.getPublicUrl(path)
      } catch (e) {
        await cleanup(row, path)
        fail(row, describeError(e))
        return
      }
      const check = validatePublicUrl(publicUrl, deps.storageOrigin, extForMime(processed.mime))
      if (!check.ok) {
        await cleanup(row, path)
        fail(row, check.reason)
        return
      }

      patch(row, { state: 'saving' })
      await saveRow(row, attempt, path, publicUrl, index)
    } catch (e) {
      // A row that reached `done` COMMITTED: its object is the photo, and
      // deleting it would break a live album entry over a render bug.
      if (row.state !== 'done' && !compensated.has(path)) await cleanup(row, path)
      throw e
    }
  }

  /**
   * The worker's only exit. Nothing a caller injected - a dep, or the onRow /
   * onPause / onAbort callbacks the page will wire to React state setters - may
   * reject start() or leave a row in a non-terminal state it can never be
   * restarted from.
   */
  const runRow = async (row: BulkRow): Promise<void> => {
    try {
      await runOneRow(row)
    } catch (e) {
      const reason = describeError(e)
      console.warn(`[bulk-upload] row ${row.id} crashed`, reason)
      // A row that already reached a terminal state KEEPS it. The throw is
      // logged, not laundered into an upload failure: a page callback that
      // explodes while rendering `done` must not erase a photo PostgREST has
      // already written, because outcome() derives both the count and the id
      // from state === 'done' (EC-21). Same for skipped, and for a row already
      // failed with a reason an operator can act on.
      if (TERMINAL.includes(row.state)) return
      patchQuietly(row, { state: 'failed', reason })
    }
  }

  const worker = async (pending: BulkRow[]): Promise<void> => {
    for (;;) {
      // The one place `aborted` belongs: stop STARTING files.
      if (cancelled || paused || aborted) return
      const row = pending.shift()
      if (!row) return
      if (row.state !== 'queued') continue
      await runRow(row)
    }
  }

  const outcome = (): BatchOutcome => {
    const count = (state: RowState) => rows.filter((r) => r.state === state).length
    return {
      done: count('done'),
      failed: count('failed'),
      skipped: count('skipped'),
      cancelled: count('cancelled'),
      asIsCount: rows.filter((r) => r.state === 'done' && r.asIs).length,
      // DERIVED, not accumulated, so photoIds and `done` can never disagree -
      // EC-21's "committed rows are listed" is then true by construction, and
      // it stays true across the passes of an Upload-remaining retry.
      photoIds: rows.flatMap((r) => (r.state === 'done' && r.photoId ? [r.photoId] : [])),
      orphanPaths: [...orphanPaths],
      errorCodes: [...errorCodes],
      uploadedBytes,
    }
  }

  const markQueuedAsCancelled = (): void => {
    for (const row of rows) if (row.state === 'queued') patchQuietly(row, { state: 'cancelled' })
  }

  return {
    start(): Promise<BatchOutcome> {
      // Synchronous guard, BEFORE any await: a double-clicked Start must not
      // launch a second pass over the same rows.
      if (running) return inFlight ?? Promise.resolve(outcome())
      running = true
      cancelled = false
      paused = false
      aborted = false
      consecutiveNetwork = 0
      lastCode = ''
      consecutiveSameCode = 0
      // Per-pass, so a Retry reports what THIS pass did: otherwise its
      // errorCodes carry the previous pass's failures and its uploadedBytes
      // double-counts every row whose PUT succeeded and was then re-uploaded,
      // which is the number EC-78 has the operator compare against the plan.
      errorCodes.length = 0
      uploadedBytes = 0

      // Startable states are exactly queued, cancelled and failed. Never done,
      // skipped, uploading or saving.
      const pending = rows.filter((r) => r.state === 'queued' || r.state === 'cancelled' || r.state === 'failed')
      for (const row of pending) if (row.state !== 'queued') patchQuietly(row, { state: 'queued', reason: undefined })

      const workerCount = Math.max(1, Math.min(slots, pending.length))
      const workers = Array.from({ length: workerCount }, () => worker(pending))

      // finally, not then: isRunning() must go false on EVERY exit path, or the
      // page's Start button never re-enables and start() returns a stale outcome
      // for the rest of the session.
      inFlight = Promise.all(workers)
        .catch((e) => {
          emit('worker', () => {
            console.warn('[bulk-upload] worker crashed', describeError(e))
          })
        })
        .finally(() => {
          emit('teardown', () => {
            running = false
            inFlight = null
          })
        })
        .then(() => {
          // Guarded like every other caller callback: markQueuedAsCancelled
          // patches rows, which calls onRow, which is the page's React setter.
          emit('cancel-sweep', () => {
            if (cancelled) markQueuedAsCancelled()
          })
          return outcome()
        })
      return inFlight
    },

    cancel(): void {
      cancelled = true
      markQueuedAsCancelled()
    },

    resume(): void {
      paused = false
    },

    setAlbumId(next: string): void {
      albumId = next
    },

    keepAnyway(id: string): void {
      const row = rows.find((r) => r.id === id)
      if (!row || row.state !== 'skipped') return
      patchQuietly(row, { state: 'queued', reason: undefined, keepAnyway: true })
    },

    isRunning(): boolean {
      return running
    },
  }
}
